import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { GeneralLedgerService } from '../../services/general-ledger.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Location } from '@angular/common';
import { GlobalSearchService } from '../../services/globalsearchservice.service';
import { trigger, state, style, transition, animate } from '@angular/animations';

@Component({
	selector: 'app-general-posting-setup',
	templateUrl: './general-posting-setup.component.html',
	styleUrls: ['./general-posting-setup.component.scss'],
	animations: [
		trigger('expandCollapse', [
			state('void', style({ height: '0px', opacity: 0, overflow: 'hidden' })),
			state('*', style({ height: '*', opacity: 1 })),
			transition('void <=> *', [animate('300ms ease-in-out')]),
		]),
	],
})
export class GeneralPostingSetupComponent implements OnInit {
	form: FormGroup;
	rows: any[] = [];
	glCodesWithZero: { code: string; name: string }[] = [];
	editingRow: any = null;
	isLoading = false;
	color = 'blue';
	isFormVisible = false;
	searchTerm = '';

	readonly accountFields: { key: string; label: string }[] = [
		{ key: 'sales_account', label: 'Sales account' },
		{ key: 'sales_credit_memo_account', label: 'Sales credit memo account' },
		{ key: 'sales_line_disc_account', label: 'Sales line discount account' },
		{ key: 'sales_inv_disc_account', label: 'Sales invoice discount account' },
		{ key: 'sales_pmt_tol_debit_account', label: 'Sales pmt. tolerance debit' },
		{ key: 'sales_pmt_tol_credit_account', label: 'Sales pmt. tolerance credit' },
		{ key: 'purch_account', label: 'Purchases account' },
		{ key: 'purch_credit_memo_account', label: 'Purchase credit memo account' },
		{ key: 'purch_line_disc_account', label: 'Purchase line discount account' },
		{ key: 'purch_inv_disc_account', label: 'Purchase invoice discount account' },
		{ key: 'purch_pmt_tol_debit_account', label: 'Purchase pmt. tolerance debit' },
		{ key: 'purch_pmt_tol_credit_account', label: 'Purchase pmt. tolerance credit' },
		{ key: 'purch_prepayments_account', label: 'Purchase prepayments account' },
		{ key: 'cogs_account', label: 'COGS account' },
		{ key: 'cogs_account_interim', label: 'COGS interim account' },
		{ key: 'inventory_adjmt_account', label: 'Inventory adjustment account' },
		{ key: 'invt_accrual_interim_account', label: 'Inventory accrual interim account' },
		{ key: 'direct_cost_applied_account', label: 'Direct cost applied account' },
		{ key: 'overhead_applied_account', label: 'Overhead applied account' },
		{ key: 'purchase_variance_account', label: 'Purchase variance account' },
		{ key: 'purch_account_allocation', label: 'Purchase allocation account' },
	];

	constructor(
		private fb: FormBuilder,
		private glService: GeneralLedgerService,
		private snackBar: MatSnackBar,
		private location: Location,
		private globalSearchService: GlobalSearchService,
	) {
		this.color = this.globalSearchService.getColor();
		const controls: Record<string, unknown> = {
			gen_bus_posting_group: ['', Validators.required],
			gen_prod_posting_group: ['', Validators.required],
			description: [''],
			blocked: [false],
		};
		for (const f of this.accountFields) {
			controls[f.key] = ['0', Validators.required];
		}
		this.form = this.fb.group(controls);
	}

	ngOnInit(): void {
		this.loadRows();
		this.loadGlCodes();
	}

	toggleForm(): void {
		this.isFormVisible = !this.isFormVisible;
	}

	filteredRows(): any[] {
		const q = (this.searchTerm || '').toLowerCase().trim();
		if (!q) {
			return this.rows;
		}
		return this.rows.filter((row) =>
			Object.values(row).some((value) => value != null && String(value).toLowerCase().includes(q)),
		);
	}

	loadGlCodes(): void {
		this.glService.fetchGeneralPostingSetupGlCodes().subscribe(
			(res: any) => {
				const list = (res.glCodes || []).map((c: any) => ({ code: String(c.code), name: c.name || '' }));
				this.glCodesWithZero = [{ code: '0', name: '— (none / default)' }, ...list];
			},
			() => {
				this.glCodesWithZero = [{ code: '0', name: '— (none / default)' }];
			},
		);
	}

	loadRows(): void {
		this.isLoading = true;
		this.glService.fetchGeneralPostingSetup({}).subscribe(
			(response: any) => {
				this.rows = response.data || [];
				this.isLoading = false;
			},
			() => {
				this.snackBar.open('Error loading general posting setup.', 'Close', { duration: 4000 });
				this.rows = [];
				this.isLoading = false;
			},
		);
	}

	resetForm(): void {
		this.editingRow = null;
		this.form.reset({
			gen_bus_posting_group: '',
			gen_prod_posting_group: '',
			description: '',
			blocked: false,
		});
		for (const f of this.accountFields) {
			this.form.get(f.key)?.setValue('0');
		}
		this.form.get('gen_bus_posting_group')?.enable();
		this.form.get('gen_prod_posting_group')?.enable();
	}

	openNew(): void {
		this.resetForm();
		this.isFormVisible = true;
	}

	editRow(row: any): void {
		this.editingRow = row;
		const blocked = row.blocked === 1 || row.blocked === true || row.blocked === '1';
		const patch: any = {
			gen_bus_posting_group: row.gen_bus_posting_group,
			gen_prod_posting_group: row.gen_prod_posting_group,
			description: row.description || '',
			blocked,
		};
		for (const f of this.accountFields) {
			const v = row[f.key];
			patch[f.key] = v != null && String(v) !== '' ? String(v) : '0';
		}
		this.form.patchValue(patch);
		this.form.get('gen_bus_posting_group')?.disable();
		this.form.get('gen_prod_posting_group')?.disable();
		this.isFormVisible = true;
	}

	save(): void {
		const raw = this.form.getRawValue();
		if (this.form.invalid) {
			this.globalSearchService.getFormValidationErrors(this.form);
			return;
		}
		const payload: any = {
			...raw,
			blocked: raw.blocked ? 1 : 0,
		};
		if (this.editingRow) {
			payload.orig_gen_bus_posting_group = this.editingRow.gen_bus_posting_group;
			payload.orig_gen_prod_posting_group = this.editingRow.gen_prod_posting_group;
		}
		const req = this.editingRow
			? this.glService.updateGeneralPostingSetup(payload)
			: this.glService.addGeneralPostingSetup(payload);
		req.subscribe(
			(r: any) => {
				if (r.error) {
					this.snackBar.open(r.error, 'Close', { duration: 5000 });
					return;
				}
				this.snackBar.open(r.message || 'Saved.', 'Close', { duration: 3000 });
				this.isFormVisible = false;
				this.resetForm();
				this.loadRows();
			},
			() => this.snackBar.open('Save failed.', 'Close', { duration: 4000 }),
		);
	}

	deleteRow(row: any): void {
		if (!confirm('Delete this general posting setup row?')) {
			return;
		}
		this.glService
			.deleteGeneralPostingSetup({
				gen_bus_posting_group: row.gen_bus_posting_group,
				gen_prod_posting_group: row.gen_prod_posting_group,
			})
			.subscribe(
				() => {
					this.snackBar.open('Deleted.', 'Close', { duration: 3000 });
					this.loadRows();
				},
				() => this.snackBar.open('Delete failed.', 'Close', { duration: 4000 }),
			);
	}

	back(): void {
		this.location.back();
	}

	exportExcel(): void {
		const list = this.filteredRows();
		if (!list.length) {
			return;
		}
		const keys = [
			'gen_bus_posting_group',
			'gen_prod_posting_group',
			'description',
			'blocked',
			...this.accountFields.map((f) => f.key),
		];
		const rows = list.map((p) => {
			const o: Record<string, unknown> = {};
			for (const k of keys) {
				o[k] = p[k];
			}
			return o;
		});
		const title = 'General Posting Setup';
		const subtitle = 'Exported: ' + new Date().toLocaleString();
		this.globalSearchService.exportJsonToExcelWithTitle(
			title,
			subtitle,
			rows,
			'GeneralPostingSetup_' + new Date().toISOString().slice(0, 10),
		);
	}
}
